var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import classNames from 'clsx';
import DetectOutsideClick from '../detect-outside-click';
import Icon from '../icon';
import { Portal } from '../portal';
import { DisableScroll } from '../../utilities/disable-scrolling';
import { useEscapeHandler } from '../../utilities/use-escape-handler';
import useIsScrollable from '../../utilities/use-is-scrollable';
import { ModalClose, ModalContainer, ModalContent, ModalFooter, ModalFooterButton, ModalHeader, ModalTitle, ModalWrapper, } from './styles/modal.styled';
/**
 * A modal is a lightbox displaying content on top of the current page
 */
const Modal = forwardRef((_a, ref) => {
    var _b;
    var { children, variant = 'passive', size = 's', short, fullSize, buttonText = 'Button text', buttonVisualStyle = 'primary', cancelButtonText = 'Button text', buttonHandler, onOpenChange, open, class: className, title, tabIndex } = _a, props = __rest(_a, ["children", "variant", "size", "short", "fullSize", "buttonText", "buttonVisualStyle", "cancelButtonText", "buttonHandler", "onOpenChange", "open", "class", "title", "tabIndex"]);
    const modalWrapperRef = useRef(null);
    const closeButtonRef = useRef(null);
    const previouslyFocusedElementRef = useRef(null);
    const modalContentRef = useRef(null);
    const footerRef = useRef(null);
    const { isScrollable, isScrolled } = useIsScrollable(modalContentRef);
    const [showFade, setShowFade] = useState(isScrollable);
    const modalClassNames = classNames('PONCHO-modal', className);
    const focusTrap = useCallback(({ tabbableElements, direction }) => {
        if (direction === 'forward') {
            tabbableElements[0].focus();
        }
        else {
            tabbableElements[tabbableElements.length - 1].focus();
        }
    }, []);
    useEffect(() => {
        setShowFade(isScrollable);
    }, [isScrollable]);
    useEffect(() => {
        if (open) {
            previouslyFocusedElementRef.current = document.activeElement;
            if (closeButtonRef.current) {
                closeButtonRef.current.focus();
            }
            else if (modalWrapperRef.current) {
                modalWrapperRef.current.focus();
            }
        }
        else if (previouslyFocusedElementRef.current) {
            previouslyFocusedElementRef.current.focus();
        }
    }, [open]);
    const handleEscape = () => {
        onOpenChange(false);
    };
    useEscapeHandler({ shouldListen: open, handleEscape });
    const handleScroll = (node) => {
        const { scrollHeight, scrollTop, clientHeight } = node;
        setShowFade(scrollHeight - scrollTop !== clientHeight);
    };
    const footerAffirmativeButtonOnClick = () => {
        if (buttonHandler) {
            buttonHandler();
        }
        else {
            onOpenChange(false);
        }
    };
    const footerAffirmativeButtonOnKeyDown = ({ key }) => {
        if (key === 'Enter') {
            footerAffirmativeButtonOnClick();
        }
    };
    const footerDismissiveButtonOnKeyDown = ({ key }) => {
        if (key === 'Enter') {
            onOpenChange(false);
        }
    };
    if (!open) {
        return null;
    }
    return (_jsxs(Portal, { id: "modal", className: "PONCHO-modal-container", onTabOut: focusTrap, children: [_jsx(DisableScroll, {}), _jsx(ModalContainer, Object.assign({ ref: ref, className: modalClassNames }, props, { children: _jsx(DetectOutsideClick, { onClickOutside: () => {
                        onOpenChange(false);
                    }, children: _jsxs(ModalWrapper, { ref: modalWrapperRef, className: "PONCHO-modal__wrapper", "$size": size, "$fullSize": fullSize, "$short": short, "$showFade": showFade, "$fadeOffset": ((_b = footerRef.current) === null || _b === void 0 ? void 0 : _b.scrollHeight) || 0, tabIndex: tabIndex, children: [_jsxs(ModalHeader, { className: "PONCHO-modal__header", "$fullSize": fullSize, "$isScrollable": isScrollable, "$isScrolled": isScrolled, children: [title && _jsx(ModalTitle, { variant: "component-title", children: title }), _jsx(ModalClose, { ref: closeButtonRef, className: "PONCHO-modal__close", onClick: () => {
                                            onOpenChange(false);
                                        }, type: "button", children: _jsx(Icon, { variant: "cross", size: "sm" }) })] }), _jsx(ModalContent, Object.assign({ ref: modalContentRef, className: "PONCHO-modal__content" }, (isScrollable
                                ? {
                                    onScroll: (event) => {
                                        handleScroll(event.target);
                                    },
                                }
                                : {}), { "$fullSize": fullSize, "$isScrollable": isScrollable, children: children })), variant === 'dialog' && (_jsxs(ModalFooter, { ref: footerRef, className: "PONCHO-modal__footer", "$isScrollable": isScrollable, "$isScrolled": isScrolled, children: [_jsx(ModalFooterButton, { size: "small", color: "tertiary", onClick: () => {
                                            onOpenChange(false);
                                        }, onKeyDown: footerDismissiveButtonOnKeyDown, children: cancelButtonText }), _jsx(ModalFooterButton, { size: "small", color: buttonVisualStyle, onClick: footerAffirmativeButtonOnClick, onKeyDown: footerAffirmativeButtonOnKeyDown, children: buttonText })] }))] }) }) }))] }));
});
export default Modal;
